import type {
  AnalyticsOverview,
  CachSapSoSanh,
  ChuoiChiSo,
  DoanhThuNhieuKenh,
  ListChannelsResponse,
  MaChiSo,
  NguoiXemNhieuKenh,
  NoiDungNhieuKenh,
  SoSanhKenh,
} from '@qlk/shared';
import { useQuery } from '@tanstack/react-query';

import { api, type LoiApi } from '../../lib/api.js';

const GOC = '/api/v1/analytics';

/**
 * Hook của **trang Hiệu suất mới** (`/phan-tich`).
 *
 * Khác `api.ts` ở một chỗ quan trọng: ba endpoint này nhận **mã kỳ** và **mã
 * phạm vi**, không nhận `from`/`to`. Khoảng ngày, kỳ trước và danh sách kênh
 * suy một lần ở máy chủ, nên không có hai nơi cùng suy rồi lệch nhau một ngày
 * — đúng bài học của `khoangChiaCuaKy` (29/10/2026).
 */

export const KHOA_HIEU_SUAT = {
  chiSo: (ky: string, phamVi: string) => ['analytics', 'hieu-suat', 'chi-so', ky, phamVi] as const,
  chuoi: (ky: string, phamVi: string, chiSo: string) =>
    ['analytics', 'hieu-suat', 'chuoi', ky, phamVi, chiSo] as const,
  soSanh: (ky: string, phamVi: string, chiSo: string, sap: string, limit: number) =>
    ['analytics', 'hieu-suat', 'so-sanh', ky, phamVi, chiSo, sap, limit] as const,
  kenhToiQuanLy: ['analytics', 'hieu-suat', 'kenh-toi-quan-ly'] as const,
  noiDung: (ky: string, phamVi: string, limit: number) =>
    ['analytics', 'hieu-suat', 'noi-dung', ky, phamVi, limit] as const,
  nguoiXem: (ky: string, phamVi: string) =>
    ['analytics', 'hieu-suat', 'nguoi-xem', ky, phamVi] as const,
  doanhThu: (ky: string, phamVi: string) =>
    ['analytics', 'hieu-suat', 'doanh-thu', ky, phamVi] as const,
};

function thamSo(phan: Record<string, string | number>): string {
  const sp = new URLSearchParams();
  for (const [k, v] of Object.entries(phan)) sp.set(k, String(v));
  return `?${sp.toString()}`;
}

/** Tám ô chỉ số của kỳ và phạm vi đang chọn. */
export function useChiSoHieuSuat(ky: string, phamVi: string, bat = true) {
  return useQuery<AnalyticsOverview, LoiApi>({
    queryKey: KHOA_HIEU_SUAT.chiSo(ky, phamVi),
    queryFn: () => api.get<AnalyticsOverview>(`${GOC}/chi-so${thamSo({ ky, 'pham-vi': phamVi })}`),
    enabled: bat && ky !== '',
  });
}

/** Hai chuỗi theo ngày của chỉ số đang chọn: kỳ này và kỳ trước. */
export function useChuoiChiSo(ky: string, phamVi: string, chiSo: MaChiSo, bat = true) {
  return useQuery<ChuoiChiSo, LoiApi>({
    queryKey: KHOA_HIEU_SUAT.chuoi(ky, phamVi, chiSo),
    queryFn: () =>
      api.get<ChuoiChiSo>(
        `${GOC}/chuoi-chi-so${thamSo({ ky, 'pham-vi': phamVi, 'chi-so': chiSo })}`,
      ),
    enabled: bat && ky !== '',
  });
}

/** Khung so sánh kênh. `limit` lớn hơn khi người dùng đã bấm "Xem tất cả". */
export function useSoSanhKenh(
  ky: string,
  phamVi: string,
  chiSo: MaChiSo,
  sap: CachSapSoSanh,
  limit: number,
  bat = true,
) {
  return useQuery<SoSanhKenh, LoiApi>({
    queryKey: KHOA_HIEU_SUAT.soSanh(ky, phamVi, chiSo, sap, limit),
    queryFn: () =>
      api.get<SoSanhKenh>(
        `${GOC}/so-sanh-kenh${thamSo({ ky, 'pham-vi': phamVi, 'chi-so': chiSo, sap, limit })}`,
      ),
    enabled: bat && ky !== '',
  });
}

/**
 * Người đang xem có phụ trách kênh nào hay không — quyết định **hiện hay ẩn**
 * chip "Kênh tôi quản lý".
 *
 * Hỏi `channels` bằng đúng bộ lọc mà phạm vi `toi-quan-ly` dùng ở máy chủ
 * (`nguoiId` của mình + vai trò quản lý/trưởng nhóm), `limit=1` vì chỉ cần biết
 * có hay không. Thiếu quyền xem kênh thì không gọi, và chip không hiện.
 */
export function useCoKenhToiQuanLy(userId: string | undefined, bat: boolean) {
  return useQuery<ListChannelsResponse, LoiApi>({
    queryKey: [...KHOA_HIEU_SUAT.kenhToiQuanLy, userId ?? ''],
    queryFn: () =>
      api.get<ListChannelsResponse>(
        `/api/v1/channels${thamSo({
          nguoiId: userId ?? '',
          vaiTroChia: 'quan_ly,truong_nhom',
          limit: 1,
          period: '28d',
        })}`,
      ),
    enabled: bat && userId !== undefined && userId !== '',
    staleTime: 60_000,
  });
}

/* ------------------------------------------------------------------ */
/* Ba tab gộp nhiều kênh                                               */
/* ------------------------------------------------------------------ */

/** Tab "Nội dung". `limit` tăng khi người dùng bấm "Xem tất cả". */
export function useNoiDungNhieuKenh(ky: string, phamVi: string, limit: number, bat = true) {
  return useQuery<NoiDungNhieuKenh, LoiApi>({
    queryKey: KHOA_HIEU_SUAT.noiDung(ky, phamVi, limit),
    queryFn: () =>
      api.get<NoiDungNhieuKenh>(`${GOC}/noi-dung${thamSo({ ky, 'pham-vi': phamVi, limit })}`),
    enabled: bat && ky !== '',
  });
}

/** Tab "Người xem". */
export function useNguoiXemNhieuKenh(ky: string, phamVi: string, bat = true) {
  return useQuery<NguoiXemNhieuKenh, LoiApi>({
    queryKey: KHOA_HIEU_SUAT.nguoiXem(ky, phamVi),
    queryFn: () =>
      api.get<NguoiXemNhieuKenh>(`${GOC}/nguoi-xem${thamSo({ ky, 'pham-vi': phamVi })}`),
    enabled: bat && ky !== '',
  });
}

/**
 * Tab "Doanh thu".
 *
 * `bat` phải gồm cả `revenue:view`: endpoint trả **403** cho người thiếu quyền,
 * nên gọi khi chưa có quyền là bắn một request chắc chắn lỗi mỗi lần mở trang.
 */
export function useDoanhThuNhieuKenh(ky: string, phamVi: string, bat = true) {
  return useQuery<DoanhThuNhieuKenh, LoiApi>({
    queryKey: KHOA_HIEU_SUAT.doanhThu(ky, phamVi),
    queryFn: () =>
      api.get<DoanhThuNhieuKenh>(`${GOC}/doanh-thu${thamSo({ ky, 'pham-vi': phamVi })}`),
    enabled: bat && ky !== '',
  });
}
